
import { defineComponent, onBeforeMount, reactive, computed } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheTable from '@/common/components/UI/TheTable/index.vue'
import TableScrollWrapper from '@/common/components/UI/TableScrollWrapper/index.vue'
import FailedFetch from '@/common/components/UI/FailedFetch/index.vue'
import Cheap from '@/common/components/UI/Cheap/index.vue'

// module components
import OrderActionsButton from '@/modules/OrderModule/components/OrderActionsButton/index.vue'
import InvoiceDocument from '@/modules/DocumentsModule/components/InvoiceDocument/index.vue'
import CommentModal from '@/modules/OrderModule/components/CommentModal/index.vue'
import ChangeDateModal from '@/modules/OrderModule/components/ChangeDateModal/index.vue'

// classes
import { DataState } from '@/core/classes/DataState.class'

// realisations
import { orders } from '@/core/realisations/orders.realisation'
import takeAPI from '@/new-services/takes.service'

// composable
import { useRoute } from 'vue-router'

import useAccount from '@/common/composable/useAccount'
import { useTitle } from 'vue-composable'
import useNotifications from '@/common/composable/useNotifications'

// types
import { OrderInvoiceT } from '@/core/types/orders.types'
import { ChangeOrderTakeDatePayload } from '@/new-services/types/takes'

// components
import { OrdersColumns } from '@/core/constants/columns/Orders.columns'
import { LtlStatusColors } from '@/core/constants/columns/LTL.columns'

import { formatDateWithTimeFromBackend, formatDateFromBackend } from '@/common/helpers/formatDate'

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        FailedFetch,
        InvoiceDocument,
        OrderActionsButton,
        CommentModal,
        TheTable,
        TableScrollWrapper,
        ChangeDateModal,
        Cheap,
    },
    setup() {
        const { userIs } = useAccount()
        const route = useRoute()
        const order_id = Number(route.params.order_id)
        const notifications = useNotifications()
        useTitle(`LTL Заказ №${order_id}`)

        const state = reactive({
            invoices: new DataState<OrderInvoiceT>({}),
            invoices_print: new DataState<OrderInvoiceT>({}),
            is_manager: false,
        })

        const comment_state = reactive({
            is_opened: false,
            is_loading: false,
        })

        const change_take_date_state = reactive({
            is_opened: false,
            is_loading: false,
            orderId: null as number | null,
        })

        const confirm_date_state = reactive({
            is_opened: false,
            is_loading: false,
            orderId: null as number | null,
        })

        //

        const loadInvoices = async (page: number) => {
            state.invoices.pagination.current_page = page

            try {
                state.invoices.is_loading = true

                const response = await orders.getOrderInvoices(order_id, {
                    page,
                    isExtended: 1,
                    limit: 9999,
                })

                state.invoices.list = response.data
                state.invoices.pagination.total_pages = response.meta.last_page

                // пользователь ответственный менеджер
                state.is_manager = state.invoices.list.every((invoice) =>
                    userIs(invoice.manager_id)
                )

                state.invoices.is_failed = false
                state.invoices.failure.error_state.is_cancel = false
            } catch (error) {
                state.invoices.failure.catchError(error)

                if (!state.invoices.failure.error_state.is_cancel) {
                    state.invoices.is_failed = true
                }
            } finally {
                if (!state.invoices.failure.error_state.is_cancel) {
                    state.invoices.is_loading = false
                }
            }
        }

        const printOrder = async () => {
            try {
                state.invoices_print.is_loading = true

                const response = await orders.getOrderInvoicesForPrint(order_id)

                state.invoices_print.list = response.data
                setTimeout(window.print, 500)
            } catch (error) {
                console.log(error)
            } finally {
                state.invoices_print.is_loading = false
            }
        }

        const toggleComment = () => (comment_state.is_opened = !comment_state.is_opened)

        const sendComment = async ({
            comment,
            is_confirmed,
        }: {
            comment: string
            is_confirmed: boolean
        }) => {
            try {
                comment_state.is_loading = true

                const payload = {
                    orderId: order_id,
                    comment,
                    isConfirmed: is_confirmed,
                }

                await orders.sendCommentOrder(payload)

                notifications.notify({ title: 'Уведомление успешно отправлено!', type: 'success' })
                toggleComment()
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При отправке уведомления произошла ошибка',
                })
            } finally {
                comment_state.is_loading = false
            }
        }

        const isEven = (n: number) => n % 2 === 0

        //

        const startChangeDate = (orderId: number) => {
            change_take_date_state.orderId = orderId
            change_take_date_state.is_opened = true
        }
        const cancelChangeDate = () => {
            change_take_date_state.orderId = null
            change_take_date_state.is_opened = false
        }

        //

        const startConfirmDate = (orderId: number) => {
            confirm_date_state.orderId = orderId
            confirm_date_state.is_opened = true
        }
        const cancelConfirmDate = () => {
            confirm_date_state.orderId = null
            confirm_date_state.is_opened = false
        }

        //

        const changeDate = async (payload: ChangeOrderTakeDatePayload) => {
            try {
                change_take_date_state.is_loading = true

                const response = await takeAPI.changeOrderTakeDate(payload)
                notifications.notify({ type: 'success', title: response.data.message })

                cancelChangeDate()

                loadInvoices(1)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При сохранении новой даты забора произошла ошибка',
                })
            } finally {
                change_take_date_state.is_loading = false
            }
        }

        //

        const confirmDate = async (payload: ChangeOrderTakeDatePayload) => {
            try {
                change_take_date_state.is_loading = true

                const params = {
                    order_id: payload.orderId,
                    new_date: payload.newDate,
                }

                const response = await takeAPI.confirmDate(params)
                notifications.notify({ type: 'success', title: response.data.message })

                cancelConfirmDate()

                loadInvoices(1)
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'При сохранении новой даты забора произошла ошибка',
                })
            } finally {
                change_take_date_state.is_loading = false
            }
        }

        const getCheapClass = (status_id: number) => {
            if (LtlStatusColors[status_id]) return LtlStatusColors[status_id]
            else if (status_id === 51) return 'red'
            else if (![1, 4].includes(status_id)) return 'yellow'

            return ''
        }

        //

        onBeforeMount(() => {
            loadInvoices(1)
        })

        //

        const getInvoiceNumber = computed(() =>
            state.invoices && state.invoices.list[0] ? state.invoices.list[0].order_number : '-'
        )

        return {
            state,
            comment_state,
            change_take_date_state,
            confirm_date_state,
            //
            sendComment,
            toggleComment,
            loadInvoices,
            isEven,
            printOrder,
            startChangeDate,
            cancelChangeDate,
            startConfirmDate,
            cancelConfirmDate,
            changeDate,
            confirmDate,
            getCheapClass,
            //
            formatDateWithTimeFromBackend,
            formatDateFromBackend,
            //
            getInvoiceNumber,
            //
            OrdersColumns,
        }
    },
})
